/**
 * NotesTab - PROJ-023 Modern Notes System
 * Full implementation with multiple attachments, drag & drop, and previews
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { MarkdownEditor, MarkdownDisplay } from '@/components/ui/markdown-editor';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  DraggableDialog,
  DraggableDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/draggable-dialog';
// Dialog, DialogContent, DialogFooter removed - using DraggableDialog for PROJ-022 compliance
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import {
  Info,
  Plus,
  MessageSquare,
  Paperclip,
  Pin,
  PinOff,
  Lock,
  Globe,
  Eye,
  Pencil,
  Trash2,
  Download,
  Upload,
  X,
  MoreVertical,
  AlertTriangle,
  Clock,
  User,
  FileText,
  Image as ImageIcon,
  File,
  CheckCircle,
  Copy,
  Loader2,
  Save
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  listNotes,
  createNote,
  updateNote,
  deleteNote,
  downloadAttachment,
  generateUploadUrl,
  uploadFileToBlob,
  createAttachment,
  calculateFileHash,
  formatFileSize,
  getFileIconType,
  isPreviewable,
  triggerDownload
} from '@/lib/notesApi';
import {
  listEvidence,
  getEvidenceDownloadUrl,
  triggerEvidenceDownload,
  formatFileSize as formatEvidenceFileSize,
  getFileIconType as getEvidenceFileIconType,
  isPreviewable as isEvidencePreviewable,
  type Evidence
} from '@/lib/evidenceApi';
import type { Note, NoteAttachment, NoteType, NotePriority, UploadingFile } from '@/types/notes';

interface NotesTabProps {
  incidentId: string | null;
}

// Note type configuration
const noteTypeConfig: Record<NoteType, { icon: any; label: string; color: string }> = {
  internal: { icon: Lock, label: 'Interno', color: 'bg-blue-100 text-blue-800' },
  client: { icon: User, label: 'Cliente', color: 'bg-green-100 text-green-800' },
  technical: { icon: FileText, label: 'Tecnico', color: 'bg-purple-100 text-purple-800' }
};

// Priority configuration
const priorityConfig: Record<NotePriority, { label: string; color: string }> = {
  low: { label: 'Baja', color: 'bg-gray-100 text-gray-800' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-800' },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-800' }
};

// File icon component
function FileIcon({ mimeType }: { mimeType?: string | null }) {
  const type = getFileIconType(mimeType);
  switch (type) {
    case 'image':
      return <ImageIcon className="h-4 w-4" />;
    case 'pdf':
      return <FileText className="h-4 w-4 text-red-500" />;
    default:
      return <File className="h-4 w-4" />;
  }
}

export function NotesTab({ incidentId }: NotesTabProps) {
  // State
  const [notes, setNotes] = useState<Note[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; fileName: string; mimeType: string } | null>(null);

  // Editor form state
  const [subject, setSubject] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<NoteType>('internal');
  const [priority, setPriority] = useState<NotePriority>('normal');
  const [isPinned, setIsPinned] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Load notes
  const loadNotes = useCallback(async () => {
    if (!incidentId) return;

    setIsLoading(true);
    try {
      const response = await listNotes(incidentId, {
        includeCreator: true,
        includeAttachments: true,
        limit: 100
      });
      setNotes(response.data || []);
    } catch (error: any) {
      console.error('Error loading notes:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar las notas',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [incidentId]);

  // Load evidence (files from forms/workflows)
  const loadEvidence = useCallback(async () => {
    if (!incidentId) return;

    setIsLoadingEvidence(true);
    try {
      const evidenceList = await listEvidence(incidentId);
      setEvidence(evidenceList);
    } catch (error: any) {
      console.error('Error loading evidence:', error);
      // Don't show toast for evidence - it's optional data
    } finally {
      setIsLoadingEvidence(false);
    }
  }, [incidentId]);

  useEffect(() => {
    if (incidentId) {
      loadNotes();
      loadEvidence();
    }
  }, [incidentId, loadNotes, loadEvidence]);

  // Reset editor form
  const resetEditorForm = () => {
    setSubject('');
    setNoteText('');
    setNoteType('internal');
    setPriority('normal');
    setIsPinned(false);
    setIsPrivate(false);
    setUploadingFiles([]);
    setEditingNote(null);
  };

  // Open editor for new note
  const openNewNoteEditor = () => {
    resetEditorForm();
    setIsEditorOpen(true);
  };

  // Open editor for editing
  const openEditNoteEditor = (note: Note) => {
    setEditingNote(note);
    setSubject(note.subject || '');
    setNoteText(note.noteText || '');
    setNoteType(note.noteType);
    setPriority(note.priority);
    setIsPinned(note.isPinned);
    setIsPrivate(note.isPrivate);
    setUploadingFiles([]);
    setIsEditorOpen(true);
  };

  // Handle file selection
  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadingFile[] = [];
    for (const file of Array.from(files)) {
      // Check file size (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: 'Archivo muy grande',
          description: `${file.name} excede el limite de 25MB`,
          variant: 'destructive'
        });
        continue;
      }

      newFiles.push({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: 'pending'
      });
    }

    setUploadingFiles(prev => [...prev, ...newFiles]);
  };

  // Remove file from upload queue
  const removeUploadingFile = (fileId: string) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Save note
  const handleSaveNote = async () => {
    if (!incidentId) return;
    if (!noteText.trim() && uploadingFiles.length === 0) {
      toast({
        title: 'Validacion',
        description: 'La nota debe tener contenido o adjuntos',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    try {
      let savedNote: Note;

      if (editingNote) {
        // Update existing note
        savedNote = await updateNote(incidentId, editingNote.id, {
          subject: subject || null,
          noteText: noteText || null,
          noteType,
          priority,
          isPinned,
          isPrivate
        });
      } else {
        // Create new note
        savedNote = await createNote(incidentId, {
          subject: subject || null,
          noteText: noteText || null,
          noteType,
          priority,
          isPinned,
          isPrivate
        });

        // Upload attachments for new note
        if (uploadingFiles.length > 0) {
          for (const uploadFile of uploadingFiles) {
            try {
              // Update status to hashing
              setUploadingFiles(prev =>
                prev.map(f => f.id === uploadFile.id ? { ...f, status: 'hashing' } : f)
              );

              // Calculate hash
              const hash = await calculateFileHash(uploadFile.file);

              // Get upload URL
              const uploadInfo = await generateUploadUrl(incidentId, uploadFile.file.name);

              // Update status to uploading
              setUploadingFiles(prev =>
                prev.map(f => f.id === uploadFile.id ? { ...f, status: 'uploading', hash } : f)
              );

              // Upload to Azure
              await uploadFileToBlob(uploadInfo.uploadUrl, uploadFile.file, (progress) => {
                setUploadingFiles(prev =>
                  prev.map(f => f.id === uploadFile.id ? { ...f, progress } : f)
                );
              });

              // Create attachment record
              await createAttachment(incidentId, savedNote.id, {
                filename: uploadFile.file.name,
                file_extension: uploadFile.file.name.split('.').pop(),
                mime_type: uploadFile.file.type,
                file_size_bytes: uploadFile.file.size,
                blob_container: 'lineage-files',
                blob_path: uploadInfo.blobPath,
                content_hash: hash
              });

              // Update status to complete
              setUploadingFiles(prev =>
                prev.map(f => f.id === uploadFile.id ? { ...f, status: 'complete', progress: 100 } : f)
              );
            } catch (error) {
              console.error('Error uploading file:', error);
              setUploadingFiles(prev =>
                prev.map(f => f.id === uploadFile.id ? { ...f, status: 'error', error: 'Error al subir archivo' } : f)
              );
            }
          }
        }
      }

      toast({
        title: 'Exito',
        description: editingNote ? 'Nota actualizada' : 'Nota creada'
      });

      setIsEditorOpen(false);
      resetEditorForm();

      // Reload notes after delay for cache sync
      await new Promise(resolve => setTimeout(resolve, 100));
      await loadNotes();
    } catch (error: any) {
      console.error('Error saving note:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar la nota',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete note
  const handleDeleteNote = async (note: Note) => {
    if (!incidentId) return;

    try {
      await deleteNote(incidentId, note.id);
      toast({
        title: 'Exito',
        description: 'Nota eliminada'
      });
      await new Promise(resolve => setTimeout(resolve, 100));
      await loadNotes();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al eliminar la nota',
        variant: 'destructive'
      });
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (note: Note, attachment: NoteAttachment) => {
    if (!incidentId) return;

    try {
      const downloadInfo = await downloadAttachment(incidentId, note.id, attachment.id);
      await triggerDownload(downloadInfo.downloadUrl, attachment.fileName);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Error al descargar el archivo',
        variant: 'destructive'
      });
    }
  };

  // Preview attachment
  const handlePreviewAttachment = async (note: Note, attachment: NoteAttachment) => {
    if (!incidentId || !isPreviewable(attachment.mimeType)) return;

    try {
      const downloadInfo = await downloadAttachment(incidentId, note.id, attachment.id);
      setPreviewAttachment({
        url: downloadInfo.downloadUrl,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType || ''
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Error al cargar la vista previa',
        variant: 'destructive'
      });
    }
  };

  // Download evidence file
  const handleDownloadEvidence = async (ev: Evidence) => {
    try {
      const downloadUrl = await getEvidenceDownloadUrl(ev.blobPath);
      await triggerEvidenceDownload(downloadUrl, ev.fileName);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Error al descargar el archivo',
        variant: 'destructive'
      });
    }
  };

  // Preview evidence file
  const handlePreviewEvidence = async (ev: Evidence) => {
    if (!isEvidencePreviewable(ev.mime)) return;

    try {
      const downloadUrl = await getEvidenceDownloadUrl(ev.blobPath);
      setPreviewAttachment({
        url: downloadUrl,
        fileName: ev.fileName,
        mimeType: ev.mime || ''
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Error al cargar la vista previa',
        variant: 'destructive'
      });
    }
  };

  // Show message for new incidents
  if (!incidentId) {
    return (
      <div className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Guarde el incidente primero para agregar notas.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Notas del Incidente</h3>
          <Badge variant="secondary">{notes.length}</Badge>
        </div>
        <Button onClick={openNewNoteEditor}>
          <Plus className="mr-2 h-4 w-4" />
          Nueva Nota
        </Button>
      </div>

      {/* Evidence Section - Files from Forms/Workflows */}
      {evidence.length > 0 && (
        <Card className="border-dashed border-amber-300 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-base text-amber-800 dark:text-amber-200">
                Documentos Adjuntos (Formulario/Workflow)
              </CardTitle>
              <Badge variant="outline" className="border-amber-400 text-amber-700">
                {evidence.length}
              </Badge>
            </div>
            <CardDescription className="text-amber-700/70 dark:text-amber-300/70">
              Archivos subidos automaticamente desde formularios o flujos de trabajo
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {isLoadingEvidence ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
              </div>
            ) : (
              <div className="grid gap-2">
                {evidence.map(ev => {
                  const iconType = getEvidenceFileIconType(ev.mime);
                  return (
                    <div
                      key={ev.id}
                      className="flex items-center justify-between bg-white dark:bg-gray-900 p-3 rounded-lg border border-amber-200 dark:border-amber-800"
                    >
                      <div className="flex items-center gap-3">
                        {iconType === 'image' && <ImageIcon className="h-5 w-5 text-blue-500" />}
                        {iconType === 'pdf' && <FileText className="h-5 w-5 text-red-500" />}
                        {iconType === 'document' && <FileText className="h-5 w-5 text-blue-600" />}
                        {iconType === 'file' && <File className="h-5 w-5 text-gray-500" />}
                        <div>
                          <p className="text-sm font-medium">{ev.fileName}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            {formatEvidenceFileSize(ev.sizeBytes)}
                            <span>-</span>
                            <Clock className="h-3 w-3" />
                            {new Date(ev.createdAt).toLocaleString('es-CL', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}
                            {ev.avStatus === 'pending' && (
                              <Badge variant="outline" className="text-xs ml-2">
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Escaneando
                              </Badge>
                            )}
                            {ev.avStatus === 'clean' && (
                              <Badge variant="outline" className="text-xs ml-2 text-green-600 border-green-300">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Seguro
                              </Badge>
                            )}
                            {ev.avStatus === 'infected' && (
                              <Badge variant="destructive" className="text-xs ml-2">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Infectado
                              </Badge>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isEvidencePreviewable(ev.mime) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePreviewEvidence(ev)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Vista previa</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadEvidence(ev)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Descargar</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && notes.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium mb-2">No hay notas</h4>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Las notas permiten documentar el progreso y mantener un registro de todas las comunicaciones.
            </p>
            <Button onClick={openNewNoteEditor}>
              <Plus className="mr-2 h-4 w-4" />
              Crear primera nota
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Notes list */}
      {!isLoading && notes.length > 0 && (
        <div className="space-y-4">
          {notes.map(note => {
            const typeConfig = noteTypeConfig[note.noteType];
            const prioConfig = priorityConfig[note.priority];
            const TypeIcon = typeConfig.icon;

            return (
              <Card key={note.id} className={cn(note.isPinned && 'border-primary')}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {note.isPinned && (
                          <Pin className="h-4 w-4 text-primary" />
                        )}
                        <CardTitle className="text-base">
                          {note.subject || 'Sin asunto'}
                        </CardTitle>
                        <Badge className={cn('text-xs', typeConfig.color)}>
                          <TypeIcon className="mr-1 h-3 w-3" />
                          {typeConfig.label}
                        </Badge>
                        {note.priority !== 'normal' && (
                          <Badge className={cn('text-xs', prioConfig.color)}>
                            {prioConfig.label}
                          </Badge>
                        )}
                        {note.isPrivate && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="mr-1 h-3 w-3" />
                            Privada
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-2 text-xs">
                        <User className="h-3 w-3" />
                        {note.creator?.fullName || 'Usuario'}
                        <span>-</span>
                        <Clock className="h-3 w-3" />
                        {new Date(note.createdAt).toLocaleString('es-CL', {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })}
                      </CardDescription>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedNote(note)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalle
                        </DropdownMenuItem>
                        {note.isEditable && (
                          <DropdownMenuItem onClick={() => openEditNoteEditor(note)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDeleteNote(note)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Note text - Rendered Markdown (PROJ-023) */}
                  {note.noteText && (
                    <div className="mb-3">
                      <MarkdownDisplay content={note.noteText} className="text-sm" />
                    </div>
                  )}

                  {/* Attachments */}
                  {note.attachmentCount > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Paperclip className="h-3 w-3" />
                        Adjuntos ({note.attachmentCount})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {note.attachments.map(att => (
                          <div
                            key={att.id}
                            className="flex items-center gap-2 bg-muted px-2 py-1 rounded text-xs"
                          >
                            <FileIcon mimeType={att.mimeType} />
                            <span className="truncate max-w-[150px]">{att.fileName}</span>
                            <span className="text-muted-foreground">
                              ({formatFileSize(att.fileSizeBytes)})
                            </span>
                            {att.isDuplicate && (
                              <Badge variant="outline" className="text-xs">
                                <Copy className="mr-1 h-3 w-3" />
                                Dup
                              </Badge>
                            )}
                            <div className="flex gap-1">
                              {isPreviewable(att.mimeType) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={() => handlePreviewAttachment(note, att)}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                onClick={() => handleDownloadAttachment(note, att)}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Note Editor Dialog */}
      <DraggableDialog open={isEditorOpen} onOpenChange={(open) => {
        if (!open) {
          resetEditorForm();
        }
        setIsEditorOpen(open);
      }}>
        <DraggableDialogContent className="flex flex-col max-h-[90vh] w-full max-w-2xl">
          {/* Header - PROJ-022 Standard */}
          <div className="px-6 py-4 border-b bg-background">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {editingNote ? 'Editar Nota' : 'Nueva Nota'}
              </DialogTitle>
              <DialogDescription>
                {editingNote
                  ? 'Modifica los campos de la nota (disponible por 15 minutos desde la creación).'
                  : 'Completa la información para agregar una nueva nota al incidente.'}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4" onWheelCapture={(e) => e.stopPropagation()}>
            <div className="space-y-6 pb-4">
            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Asunto</Label>
              <Input
                id="subject"
                placeholder="Asunto de la nota"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Note text - Rich Text Editor (PROJ-023) */}
            <div className="space-y-2">
              <Label htmlFor="noteText">Contenido *</Label>
              <MarkdownEditor
                value={noteText}
                onChange={setNoteText}
                placeholder="Escribe el contenido de la nota... (soporta Markdown)"
                minHeight="120px"
                maxHeight="250px"
              />
              <p className="text-xs text-muted-foreground">
                Usa Markdown para dar formato: **negrita**, _cursiva_, # títulos, - listas
              </p>
            </div>

            {/* Type and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Nota</Label>
                <Select value={noteType} onValueChange={(v) => setNoteType(v as NoteType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        Interno
                      </div>
                    </SelectItem>
                    <SelectItem value="client">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Cliente
                      </div>
                    </SelectItem>
                    <SelectItem value="technical">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Tecnico
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as NotePriority)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="flex gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPinned"
                  checked={isPinned}
                  onCheckedChange={(checked) => setIsPinned(checked === true)}
                />
                <Label htmlFor="isPinned" className="flex items-center gap-1 cursor-pointer">
                  <Pin className="h-4 w-4" />
                  Fijar nota
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPrivate"
                  checked={isPrivate}
                  onCheckedChange={(checked) => setIsPrivate(checked === true)}
                />
                <Label htmlFor="isPrivate" className="flex items-center gap-1 cursor-pointer">
                  <Lock className="h-4 w-4" />
                  Nota privada
                </Label>
              </div>
            </div>

            {/* File upload (only for new notes) */}
            {!editingNote && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Adjuntos</Label>
                  <div
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => document.getElementById('file-input')?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('bg-muted/50');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('bg-muted/50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-muted/50');
                      handleFileSelect(e.dataTransfer.files);
                    }}
                  >
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Arrastra archivos aqui o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOC, XLS, JPG, PNG (max. 25MB)
                    </p>
                  </div>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />

                  {/* Upload queue */}
                  {uploadingFiles.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {uploadingFiles.map(file => (
                        <div key={file.id} className="flex items-center gap-2 bg-muted p-2 rounded">
                          <FileIcon mimeType={file.file.type} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{file.file.name}</p>
                            <div className="flex items-center gap-2">
                              {file.status === 'pending' && (
                                <span className="text-xs text-muted-foreground">Pendiente</span>
                              )}
                              {file.status === 'hashing' && (
                                <span className="text-xs text-muted-foreground">Calculando hash...</span>
                              )}
                              {file.status === 'uploading' && (
                                <>
                                  <Progress value={file.progress} className="h-1 flex-1" />
                                  <span className="text-xs">{file.progress}%</span>
                                </>
                              )}
                              {file.status === 'complete' && (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" />
                                  Completado
                                </span>
                              )}
                              {file.status === 'error' && (
                                <span className="text-xs text-red-600 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  {file.error}
                                </span>
                              )}
                              {file.status === 'duplicate' && (
                                <span className="text-xs text-yellow-600 flex items-center gap-1">
                                  <Copy className="h-3 w-3" />
                                  Duplicado
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatFileSize(file.file.size)}
                          </span>
                          {file.status !== 'uploading' && file.status !== 'complete' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeUploadingFile(file.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            </div>
          </div>

          {/* Footer Actions - PROJ-022 Standard */}
          <TooltipProvider>
            <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
              <div className="flex gap-2" />
              <div className="flex items-center gap-2">
                <Button onClick={handleSaveNote} disabled={isSaving} className="gap-2">
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button onClick={async () => {
                  await handleSaveNote();
                  if (!isSaving) setIsEditorOpen(false);
                }} disabled={isSaving} className="gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Guardar y Cerrar
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setIsEditorOpen(false)} disabled={isSaving} aria-label="Cerrar">
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cerrar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* Note Detail Dialog */}
      <DraggableDialog open={!!selectedNote} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DraggableDialogContent className="flex flex-col max-h-[90vh] w-full max-w-2xl">
          {/* Header - PROJ-022 Standard */}
          <div className="px-6 py-4 border-b bg-background">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {selectedNote?.isPinned && <Pin className="h-4 w-4 text-primary" />}
                {selectedNote?.subject || 'Sin asunto'}
              </DialogTitle>
              <DialogDescription>
                Detalle completo de la nota del incidente.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4" onWheelCapture={(e) => e.stopPropagation()}>
          {selectedNote && (
            <div className="space-y-4 pb-4">
              {/* Metadata */}
              <div className="flex flex-wrap gap-2">
                <Badge className={noteTypeConfig[selectedNote.noteType].color}>
                  {noteTypeConfig[selectedNote.noteType].label}
                </Badge>
                <Badge className={priorityConfig[selectedNote.priority].color}>
                  {priorityConfig[selectedNote.priority].label}
                </Badge>
                {selectedNote.isPrivate && (
                  <Badge variant="outline">
                    <Lock className="mr-1 h-3 w-3" />
                    Privada
                  </Badge>
                )}
              </div>

              {/* Creator info */}
              <div className="text-sm text-muted-foreground">
                Creado por {selectedNote.creator?.fullName || 'Usuario'} el{' '}
                {new Date(selectedNote.createdAt).toLocaleString('es-CL')}
                {selectedNote.modifiedAt && (
                  <>
                    <br />
                    Modificado el {new Date(selectedNote.modifiedAt).toLocaleString('es-CL')}
                  </>
                )}
              </div>

              <Separator />

              {/* Content - Rendered Markdown (PROJ-023) */}
              {selectedNote.noteText && (
                <div className="bg-muted p-4 rounded-lg">
                  <MarkdownDisplay content={selectedNote.noteText} />
                </div>
              )}

              {/* Attachments */}
              {selectedNote.attachmentCount > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Adjuntos ({selectedNote.attachmentCount})
                  </h4>
                  <div className="grid gap-2">
                    {selectedNote.attachments.map(att => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between bg-muted p-3 rounded"
                      >
                        <div className="flex items-center gap-3">
                          <FileIcon mimeType={att.mimeType} />
                          <div>
                            <p className="text-sm font-medium">{att.fileName}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(att.fileSizeBytes)}
                              {att.isDuplicate && ' - Archivo duplicado'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(att.mimeType) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreviewAttachment(selectedNote, att)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Preview
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadAttachment(selectedNote, att)}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Descargar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Footer Actions - PROJ-022 Standard */}
          <TooltipProvider>
            <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
              <div className="flex gap-2">
                {selectedNote?.isEditable && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      openEditNoteEditor(selectedNote);
                      setSelectedNote(null);
                    }}
                    className="gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    Editar
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setSelectedNote(null)} aria-label="Cerrar">
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cerrar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* Preview Dialog */}
      <DraggableDialog open={!!previewAttachment} onOpenChange={(open) => !open && setPreviewAttachment(null)}>
        <DraggableDialogContent className="flex flex-col max-h-[90vh] w-full max-w-4xl">
          {/* Header - PROJ-022 Standard */}
          <div className="px-6 py-4 border-b bg-background">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {previewAttachment?.fileName}
              </DialogTitle>
              <DialogDescription>
                Vista previa del archivo adjunto.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4" onWheelCapture={(e) => e.stopPropagation()}>
          {previewAttachment && (
            <div className="flex items-center justify-center pb-4">
              {previewAttachment.mimeType.startsWith('image/') ? (
                <img
                  src={previewAttachment.url}
                  alt={previewAttachment.fileName}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : previewAttachment.mimeType === 'application/pdf' ? (
                <iframe
                  src={previewAttachment.url}
                  className="w-full h-[70vh]"
                  title={previewAttachment.fileName}
                />
              ) : null}
            </div>
          )}
          </div>

          {/* Footer Actions - PROJ-022 Standard */}
          <TooltipProvider>
            <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (previewAttachment) {
                      triggerDownload(previewAttachment.url, previewAttachment.fileName);
                    }
                  }}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Descargar
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setPreviewAttachment(null)} aria-label="Cerrar">
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cerrar</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        </DraggableDialogContent>
      </DraggableDialog>
    </div>
  );
}
